import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorEl, anchorMissing } from './tasks-help.shared';

/**
 * ?-Hilfe fuer das Erledigen-Sheet — die Mitarbeiter-Sicht auf eine einzelne
 * Aufgabe (`pages/task-detail-sheet/task-detail-sheet.component.ts`).
 *
 * **Muster 1 — vier Modi, aber nur einer ist begehbar.**
 * Die Komponente fuehrt `type SheetMode = 'checklist' | 'confirmOpen' |
 * 'blockReason' | 'doneFeedback'` und startet mit `mode = signal('checklist')`
 * (Zeilen 81 und 828). Die drei anderen Modi entstehen erst NACH einer Aktion:
 * `confirmOpen` beim Klick auf "Erledigt" mit noch offenen Nicht-Pflicht-
 * Punkten, `blockReason` ueber "Kann ich nicht", `doneFeedback` nach dem
 * erfolgreichen Abschluss. Jeder von ihnen tauscht die Fussleiste komplett
 * (vier `*ngIf="mode() === …"`-Zweige) und ersetzt bzw. ergaenzt den Rumpf —
 * `doneFeedback` blendet Fortschritt und Checkliste ganz aus. In einem
 * linearen Durchlauf waeren eigene Schritte dafuer deshalb IMMER uebersprungen.
 * Die Tour zeigt daher nur den Checklisten-Modus und erklaert die drei
 * Folgemodi im letzten Schritt textuell auf der Fussleiste.
 *
 * **Muster 2 — eine Primaeraktion, ein Anker.**
 * "Uebernehmen" (Topf-Aufgabe) und "Erledigt" tragen beide
 * {@link AnchorId.TaskSheetCompletePrimary} und schliessen sich per `*ngIf`
 * aus; es steht immer hoechstens einer im DOM. `cs-actions` braucht deshalb
 * keine Textvarianten, nur ein `skipIf` auf diesen einen Anker.
 *
 * **Muster 3 — ein Foto zaehlt erst nach der Pruefung.**
 * `hasUsablePhotoProof` in
 * `modules/tasks/pages/task-detail-dialog/photo-proof.ts` verlangt
 * `Status === 'Uploaded'` UND `ScanStatus === 'Clean'` — genau die Bedingung,
 * die auch der Server prueft. `Pending`, `Infected` und `ScanFailed` zaehlen
 * nicht (fail-closed), der Punkt bleibt bis zur Freigabe des Scans offen.
 *
 * Dialog-Tour: alle Schritte laufen mit `popoverOutsideDialog`, damit der
 * Popover nicht genau den Punkt verdeckt, den er erklaert. `panelIndex: -1`
 * ueberall — das Sheet ist kein Material-Accordion; ohne den Wert liefe der
 * Coordinator in den Legacy-Zweig und klickte jeden Kopf an.
 *
 * **Muster 4 — dasselbe Sheet, zwei Huellen (06.10.2026, Befunde B-1/B-2).**
 * Am Telefon ist das Sheet ein MatDialog (Bottom-Sheet); ab 1024 px steht es in
 * „Meine Aufgaben" eingebettet als Seitenflaeche (`.tds-panel`), also OHNE Dialog.
 * Dieselben Anker stehen in beiden Huellen. Was sich unterscheidet:
 *  - `dialogContext` gilt nur in der Dialog-Huelle ({@link inSheetDialog}). In der
 *    Seitenflaeche wartete `waitForOpen` sonst 8 s auf einen Dialog, der nie kommt,
 *    und `popoverOutsideDialog` setzte die Karte mangels Dialog in die Bildmitte.
 *  - `launchContext.readyAnchor`: steht das Sheet schon (Start ueber „Hilfe zu
 *    diesem Fenster"), wird weder navigiert noch geoeffnet; nach dem Opener-Klick
 *    wartet der Coordinator auf den Anker statt auf einen Dialog.
 *  - `launchContext.mobileRoute`: wer am Telefon in der Mobil-Huelle steht,
 *    bleibt im Tab „Aufgaben" statt auf die Desktop-Seite `/tasks/my` ohne
 *    Tab-Leiste geschoben zu werden.
 */
const dialogStep = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

/**
 * Steht das Sheet eingebettet (Seitenflaeche, kein Dialog)? Faellt der Anker
 * (noch) nicht, gilt die Dialog-Huelle — das ist der Start aus dem Hilfe-Hub
 * am Telefon, bei dem `waitForOpen` auf den sich oeffnenden Dialog wartet.
 */
export function sheetIsEmbedded(): boolean {
  const el = anchorEl(AnchorId.TaskSheetCompleteDialog);
  if (!el) return false;
  return !el.closest('.mat-mdc-dialog-container, mat-dialog-container');
}

/** `dialogContext` nur in der Dialog-Huelle (Muster 4). */
export function inSheetDialog<T>(ctx: T): T | undefined {
  return sheetIsEmbedded() ? undefined : ctx;
}

export const TASK_COMPLETE_SHEET_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskCompleteSheetHelp,
  version: 1,
  title: 'TASK_COMPLETE_SHEET_HELP_TOUR_TITLE',
  description: 'TASK_COMPLETE_SHEET_HELP_TOUR_DESC',
  audience: [Audience.Employee, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Start aus dem Hilfe-Hub: zu "Meine Aufgaben" navigieren (am Telefon in den
  // Tab), dann den Oeffnen-Knopf der ersten Zeile klicken. Aus dem bereits
  // offenen Sheet heraus (Dialog ODER Seitenflaeche) startet die Tour an Ort und
  // Stelle — Muster 4.
  launchContext: {
    route: '/tasks/my',
    mobileRoute: '/employee-mobile/tabs/tab-tasks',
    readyAnchor: AnchorId.TaskSheetCompleteDialog,
    opens: { openerAnchor: AnchorId.MyTasksRowOpen, settleMs: 500 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 95,
  category: 'feature-discovery',
  // Sechs der acht Schritte haengen an der konkreten Aufgabe (Blockade,
  // Checkliste, Fotopunkt, Notiz, erlaubte Aktionen). Ohne Umnummerierung
  // zaehlte das Popover bei einer schlichten Aufgabe "1 / 8 → 8 / 8".
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_COMPLETE_SHEET_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'cs-welcome',
          anchor: AnchorId.TaskSheetCompleteDialog,
          spotlightAnchor: AnchorId.TaskSheetCompleteDialog,
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.WELCOME.CONTENT',
          audioStepId: 'cs-welcome-v2',
          placement: 'right',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog({
              ...dialogStep,
              waitForOpen: true,
              outsideDialog: true,
              highlightDialogHeader: true,
            });
          },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Nur bei `IsBlocked` im DOM. `warning`-Ton, weil der Schritt einen
          // Zustand zeigt, der die Aufgabe anhaelt — kein Erklaer-Schritt.
          id: 'cs-blocked',
          anchor: AnchorId.TaskSheetCompleteBlocked,
          spotlightAnchor: AnchorId.TaskSheetCompleteBlocked,
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.BLOCKED.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.BLOCKED.CONTENT',
          audioStepId: 'cs-blocked-v2',
          placement: 'top',
          tone: 'warning',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog(dialogStep);
          },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetCompleteBlocked),
        },
        {
          // Fehlt bei Aufgaben ohne Checkliste. Der Zusatz-Cutout zeigt die
          // Fortschrittsanzeige, die ueber der Liste steht.
          id: 'cs-checklist',
          anchor: AnchorId.TaskSheetCompleteList,
          spotlightAnchor: AnchorId.TaskSheetCompleteList,
          additionalSpotlightAnchors: [AnchorId.TaskSheetCompleteProgress],
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.CHECKLIST.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.CHECKLIST.CONTENT',
          audioStepId: 'cs-checklist-v2',
          placement: 'right',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog(dialogStep);
          },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetCompleteList),
        },
        {
          // Muster 3: hochgeladen UND geprueft. Der Anker sitzt am ersten
          // Fotonachweis — ohne Foto-Punkt gibt es ihn nicht.
          id: 'cs-photo',
          anchor: AnchorId.TaskSheetCompletePhoto,
          spotlightAnchor: AnchorId.TaskSheetCompletePhoto,
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.PHOTO.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.PHOTO.CONTENT',
          audioStepId: 'cs-photo-v2',
          placement: 'right',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog(dialogStep);
          },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetCompletePhoto),
        },
        {
          // Steht nur im Checklisten-Modus und nur, wenn die Aufgabe
          // ueberhaupt abschliessbar ist (`canComplete`).
          id: 'cs-note',
          anchor: AnchorId.TaskSheetCompleteNote,
          spotlightAnchor: AnchorId.TaskSheetCompleteNote,
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.NOTE.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.NOTE.CONTENT',
          audioStepId: 'cs-note-v2',
          placement: 'top',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog(dialogStep);
          },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetCompleteNote),
        },
        {
          // Muster 2: der Popover haengt an der EINEN gefuellten Aktion, der
          // Spotlight umschliesst die ganze Fussleiste — sonst sieht der
          // Nutzer die Alternativen daneben nicht.
          id: 'cs-actions',
          anchor: AnchorId.TaskSheetCompletePrimary,
          spotlightAnchor: AnchorId.TaskSheetCompleteFooter,
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.ACTIONS.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.ACTIONS.CONTENT',
          audioStepId: 'cs-actions-v2',
          placement: 'top',
          align: 'end',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog(dialogStep);
          },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetCompletePrimary),
        },
        {
          // "Geht nicht …" ist ein Menue; seine Eintraege ("Problem melden",
          // "Uebernahme zurueckgeben", …) stehen nur bei OFFENEM Menue im DOM.
          // Deshalb zeigt die Station — Spotlight wie Cue — auf den Menue-Ausloeser,
          // nicht auf `task-sheet-complete-release` (lief bis 06.10.2026 ins Leere
          // und kostete jedes Mal 2 s Wartezeit).
          id: 'cs-secondary',
          anchor: AnchorId.TaskSheetCompleteCant,
          spotlightAnchor: AnchorId.TaskSheetCompleteCant,
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.SECONDARY.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.SECONDARY.CONTENT',
          audioStepId: 'cs-secondary-v2',
          placement: 'top',
          align: 'start',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog(dialogStep);
          },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetCompleteCant),
        },
        {
          // Muster 1: die drei Folgemodi textuell auf der Fussleiste, weil sie
          // im Checklisten-Modus noch gar nicht existieren.
          id: 'cs-modes',
          anchor: AnchorId.TaskSheetCompleteFooter,
          spotlightAnchor: AnchorId.TaskSheetCompleteFooter,
          title: 'TUTORIAL.TASK_COMPLETE_SHEET.MODES.TITLE',
          content: 'TUTORIAL.TASK_COMPLETE_SHEET.MODES.CONTENT',
          audioStepId: 'cs-modes-v2',
          placement: 'top',
          panelIndex: -1,
          get dialogContext() {
            return inSheetDialog(dialogStep);
          },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskSheetCompleteFooter),
        },
      ],
    },
  ],
};
